// ============================================================================
// Inventory Module Types
// ----------------------------------------------------------------------------
// All entities are scoped by organizationCode (`org`) + restaurantId, matching
// the rest of the app (kitchens, items, tables). Stock can be transferred
// between restaurants of the same organization via StockTransfer.
// ============================================================================

// Unit of measure for stock items
export type UnitOfMeasure =
  | 'KG'
  | 'GRAM'
  | 'LITRE'
  | 'ML'
  | 'PIECE'
  | 'PACKET'
  | 'BOX'
  | 'DOZEN'
  | 'BOTTLE'
  | 'CAN'
  | 'BAG';

export const UNIT_OPTIONS: { value: UnitOfMeasure; label: string; short: string }[] = [
  { value: 'KG', label: 'Kilogram', short: 'kg' },
  { value: 'GRAM', label: 'Gram', short: 'g' },
  { value: 'LITRE', label: 'Litre', short: 'L' },
  { value: 'ML', label: 'Millilitre', short: 'ml' },
  { value: 'PIECE', label: 'Piece', short: 'pcs' },
  { value: 'PACKET', label: 'Packet', short: 'pkt' },
  { value: 'BOX', label: 'Box', short: 'box' },
  { value: 'DOZEN', label: 'Dozen', short: 'dz' },
  { value: 'BOTTLE', label: 'Bottle', short: 'btl' },
  { value: 'CAN', label: 'Can', short: 'can' },
  { value: 'BAG', label: 'Bag', short: 'bag' },
];

export function unitShort(unit?: UnitOfMeasure | string): string {
  if (!unit) return '';
  return UNIT_OPTIONS.find(u => u.value === unit)?.short ?? String(unit).toLowerCase();
}

// Stock status derived from currentStock vs reorderLevel
export type StockStatus = 'OK' | 'LOW' | 'OUT';

export function stockStatus(currentStock: number, reorderLevel: number): StockStatus {
  if (currentStock <= 0) return 'OUT';
  if (reorderLevel > 0 && currentStock <= reorderLevel) return 'LOW';
  return 'OK';
}

// ----------------------------------------------------------------------------
// Inventory Item (a tracked stock item / ingredient / supply)
// ----------------------------------------------------------------------------
export interface InventoryItem {
  id: string;
  name: string;
  sku?: string;
  category?: string;
  unit: UnitOfMeasure;
  currentStock: number;
  reorderLevel: number;      // low-stock threshold
  parLevel?: number;         // ideal stock level to maintain
  costPerUnit: number;       // average / last cost
  supplierId?: string;
  supplierName?: string;
  notes?: string;
  restaurantId: string;
  organizationCode: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateInventoryItemRequest {
  name: string;
  sku?: string;
  category?: string;
  unit: UnitOfMeasure;
  currentStock?: number;     // opening stock (creates an OPENING movement)
  reorderLevel: number;
  parLevel?: number;
  costPerUnit: number;
  supplierId?: string;
  notes?: string;
  organizationCode: string;
  restaurantId: string;
}

export interface InventoryItemFilters {
  org: string;
  restaurantId?: string;
  search?: string;
  category?: string;
  lowStock?: boolean;
  active?: boolean;
}

// ----------------------------------------------------------------------------
// Stock Movement (immutable ledger entry — every stock change is recorded)
// ----------------------------------------------------------------------------
export type StockMovementType =
  | 'OPENING'
  | 'PURCHASE_IN'
  | 'ADJUSTMENT_IN'
  | 'ADJUSTMENT_OUT'
  | 'WASTAGE'
  | 'CONSUMPTION'      // auto-deducted from recipe when an order is placed
  | 'TRANSFER_IN'
  | 'TRANSFER_OUT';

export const MOVEMENT_TYPE_META: Record<StockMovementType, { label: string; direction: 'IN' | 'OUT' }> = {
  OPENING:        { label: 'Opening Stock',   direction: 'IN' },
  PURCHASE_IN:    { label: 'Purchase',        direction: 'IN' },
  ADJUSTMENT_IN:  { label: 'Adjustment (+)',  direction: 'IN' },
  ADJUSTMENT_OUT: { label: 'Adjustment (−)',  direction: 'OUT' },
  WASTAGE:        { label: 'Wastage',         direction: 'OUT' },
  CONSUMPTION:    { label: 'Consumption',     direction: 'OUT' },
  TRANSFER_IN:    { label: 'Transfer In',     direction: 'IN' },
  TRANSFER_OUT:   { label: 'Transfer Out',    direction: 'OUT' },
};

export interface StockMovement {
  id: string;
  inventoryItemId: string;
  itemName?: string;
  unit?: UnitOfMeasure;
  type: StockMovementType;
  quantity: number;          // always positive; direction comes from `type`
  balanceAfter: number;      // stock level after this movement
  reason?: string;
  referenceType?: 'PURCHASE' | 'TRANSFER' | 'ORDER' | 'MANUAL';
  referenceId?: string;
  createdBy?: string;
  createdByName?: string;
  restaurantId: string;
  organizationCode: string;
  createdAt: string;
}

// Manual stock adjustment request (in / out / wastage)
export type AdjustmentType = 'ADJUSTMENT_IN' | 'ADJUSTMENT_OUT' | 'WASTAGE';

export interface StockAdjustmentRequest {
  type: AdjustmentType;
  quantity: number;
  reason?: string;
}

// ----------------------------------------------------------------------------
// Supplier
// ----------------------------------------------------------------------------
export interface Supplier {
  id: string;
  name: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
  address?: string;
  gstNumber?: string;
  paymentTerms?: string;
  notes?: string;
  restaurantId: string;
  organizationCode: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateSupplierRequest {
  name: string;
  contactPerson?: string;
  phone?: string;
  email?: string;
  address?: string;
  gstNumber?: string;
  paymentTerms?: string;
  notes?: string;
  organizationCode: string;
  restaurantId: string;
}

// ----------------------------------------------------------------------------
// Purchase (purchase order / goods receipt). Receiving increases stock.
// ----------------------------------------------------------------------------
export type PurchaseStatus = 'DRAFT' | 'ORDERED' | 'RECEIVED' | 'CANCELLED';
export type PaymentStatus = 'UNPAID' | 'PARTIAL' | 'PAID';

export interface PurchaseLine {
  inventoryItemId: string;
  itemName?: string;
  unit?: UnitOfMeasure;
  quantity: number;
  unitCost: number;
  lineTotal?: number;
}

export interface PurchasePayment {
  amount: number;
  mode?: string;
  note?: string;
  paidAt?: string;
}

export interface RecordPaymentRequest {
  amount: number;
  mode?: string;
  note?: string;
}

export interface SupplierOutstandingRow {
  supplierId: string;
  supplierName?: string;
  totalBilled: number;
  totalPaid: number;
  outstanding: number;
  purchaseCount: number;
}

export interface Purchase {
  id: string;
  purchaseNo?: string;
  supplierId: string;
  supplierName?: string;
  status: PurchaseStatus;
  items: PurchaseLine[];
  subTotal: number;
  taxAmount?: number;
  totalAmount: number;
  amountPaid?: number;
  paymentStatus?: PaymentStatus;
  payments?: PurchasePayment[];
  invoiceNo?: string;
  notes?: string;
  expectedDate?: string;
  receivedAt?: string;
  restaurantId: string;
  organizationCode: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreatePurchaseRequest {
  supplierId: string;
  status?: PurchaseStatus;     // DRAFT or ORDERED on create
  items: PurchaseLine[];
  taxAmount?: number;
  invoiceNo?: string;
  notes?: string;
  expectedDate?: string;
  organizationCode: string;
  restaurantId: string;
}

export interface PurchaseFilters {
  org: string;
  restaurantId?: string;
  status?: PurchaseStatus;
  supplierId?: string;
  start?: string;
  end?: string;
}

// ----------------------------------------------------------------------------
// Stock Transfer (move stock between restaurants of the same organization)
// ----------------------------------------------------------------------------
export type TransferStatus = 'PENDING' | 'COMPLETED' | 'CANCELLED';

export interface TransferLine {
  inventoryItemId: string;
  itemName?: string;
  unit?: UnitOfMeasure;
  quantity: number;
}

export interface StockTransfer {
  id: string;
  transferNo?: string;
  fromRestaurantId: string;
  fromRestaurantName?: string;
  toRestaurantId: string;
  toRestaurantName?: string;
  status: TransferStatus;
  items: TransferLine[];
  notes?: string;
  organizationCode: string;
  createdBy?: string;
  createdByName?: string;
  createdAt: string;
  completedAt?: string;
}

export interface CreateTransferRequest {
  fromRestaurantId: string;
  toRestaurantId: string;
  items: TransferLine[];
  notes?: string;
  organizationCode: string;
}

export interface TransferFilters {
  org: string;
  restaurantId?: string;          // matches either source or destination
  direction?: 'IN' | 'OUT';       // relative to restaurantId
  status?: TransferStatus;
}

// ----------------------------------------------------------------------------
// Recipe (maps a menu Item to the inventory ingredients it consumes).
// Backend auto-deducts ingredient stock (CONSUMPTION) when an order is placed.
// ----------------------------------------------------------------------------
export interface RecipeIngredient {
  inventoryItemId: string;
  itemName?: string;
  unit?: UnitOfMeasure;
  quantity: number;
}

export interface Recipe {
  id: string;
  menuItemId: string;
  menuItemName?: string;
  yieldQuantity: number;          // ingredients produce this many servings (default 1)
  ingredients: RecipeIngredient[];
  restaurantId: string;
  organizationCode: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateRecipeRequest {
  menuItemId: string;
  yieldQuantity: number;
  ingredients: RecipeIngredient[];
  organizationCode: string;
  restaurantId: string;
}

// ----------------------------------------------------------------------------
// Reports
// ----------------------------------------------------------------------------
export interface InventorySummary {
  totalItems: number;
  lowStockCount: number;
  outOfStockCount: number;
  totalStockValue: number;
}

export interface ValuationRow {
  inventoryItemId: string;
  itemName: string;
  category?: string;
  unit: UnitOfMeasure;
  currentStock: number;
  costPerUnit: number;
  stockValue: number;
}

export interface ConsumptionRow {
  inventoryItemId: string;
  itemName: string;
  unit: UnitOfMeasure;
  consumed: number;       // total consumed (recipe + adjustments out)
  wastage: number;
  purchased: number;
}

export interface ReportFilters {
  org: string;
  restaurantId?: string;
  start?: string;
  end?: string;
}

export interface ProfitabilityRow {
  menuItemId: string;
  itemName: string;
  categoryName?: string;
  sellingPrice: number;
  recipeCost: number;
  margin: number;
  marginPercent: number;
  foodCostPercent: number;
  hasRecipe: boolean;
}
